import React, { useMemo, useState } from 'react';
import { fetchKotTrackingRows } from '../../../services/reportsService';
import type { KotTrackingRow } from '../../../types/report';
import type {
  ReportColumn,
  ReportExportColumn,
  ReportMetric,
} from '../../../types/reports';
import {
  formatReportDateTime,
  formatReportMode,
  formatReportMoney,
} from '../../../util/reporting';
import { KotTrackingDetailsModal } from './KotTrackingDetailsModal';
import { ReportRecordsPage } from './ReportRecordsPage';

const buildOrderLabel = (row: KotTrackingRow): string => {
  if (row.table_name) return `${row.table_name} #${row.bill_number}`;
  return `${formatReportMode(row.mode)} #${row.bill_number}`;
};

const buildKotTrackingMetrics = (rows: KotTrackingRow[]): ReportMetric[] => {
  const missingPhysicalNumber = rows.filter(
    (row) => !row.physical_kot_number,
  ).length;
  const totalItems = rows.reduce((sum, row) => sum + row.item_count, 0);
  const totalAmount = rows.reduce((sum, row) => sum + row.total_amount, 0);

  return [
    {
      label: 'KOTs',
      value: rows.length.toLocaleString(),
      hint: 'Confirmed KOTs in the selected period',
      tone: 'emerald',
    },
    {
      label: 'Missing physical #',
      value: missingPhysicalNumber.toLocaleString(),
      hint: 'KOTs with no physical ticket number recorded',
      tone: missingPhysicalNumber > 0 ? 'rose' : 'slate',
    },
    {
      label: 'Items',
      value: totalItems.toLocaleString(),
      hint: 'Items across all KOTs in range',
      tone: 'blue',
    },
    {
      label: 'Amount',
      value: formatReportMoney(totalAmount),
      hint: 'Combined KOT item value',
      tone: 'slate',
    },
  ];
};

const kotTrackingColumns = (
  onOpen: (row: KotTrackingRow) => void,
): ReportColumn<KotTrackingRow>[] => [
  {
    key: 'confirmed_at',
    label: 'Confirmed',
    render: (row) => formatReportDateTime(row.confirmed_at),
  },
  {
    key: 'kot_id',
    label: 'System KOT #',
    render: (row) => `#${row.kot_id}`,
  },
  {
    key: 'physical_kot_number',
    label: 'Physical KOT #',
    className: 'min-w-[8rem]',
    render: (row) => (
      <button
        type="button"
        onClick={() => onOpen(row)}
        className="text-left font-semibold text-emerald-700 transition hover:text-emerald-800 hover:underline"
      >
        {row.physical_kot_number || '— missing —'}
      </button>
    ),
  },
  {
    key: 'order',
    label: 'Order',
    className: 'min-w-[10rem]',
    render: (row) => buildOrderLabel(row),
  },
  {
    key: 'waiter',
    label: 'Waiter',
    className: 'min-w-[10rem]',
    render: (row) => row.waiter_name || 'Not assigned',
  },
  {
    key: 'recorded_by',
    label: 'Recorded by',
    className: 'min-w-[10rem]',
    render: (row) => row.recorded_by_name || 'Not recorded',
  },
  {
    key: 'items',
    label: 'Items',
    align: 'right',
    render: (row) => row.item_count.toLocaleString(),
  },
  {
    key: 'amount',
    label: 'Amount',
    align: 'right',
    render: (row) => formatReportMoney(row.total_amount),
  },
];

const kotTrackingExportColumns: ReportExportColumn<KotTrackingRow>[] = [
  {
    key: 'confirmed_at',
    header: 'Confirmed',
    value: (row) => formatReportDateTime(row.confirmed_at),
  },
  { key: 'kot_id', header: 'System KOT #', value: (row) => row.kot_id },
  {
    key: 'physical_kot_number',
    header: 'Physical KOT #',
    value: (row) => row.physical_kot_number || '',
  },
  { key: 'order', header: 'Order', value: (row) => buildOrderLabel(row) },
  {
    key: 'waiter',
    header: 'Waiter',
    value: (row) => row.waiter_name || 'Not assigned',
  },
  {
    key: 'recorded_by',
    header: 'Recorded by',
    value: (row) => row.recorded_by_name || 'Not recorded',
  },
  { key: 'items', header: 'Items', value: (row) => row.item_count },
  {
    key: 'amount',
    header: 'Amount',
    value: (row) => formatReportMoney(row.total_amount),
  },
];

export const KotTrackingReportPage: React.FC = () => {
  const [selectedRow, setSelectedRow] = useState<KotTrackingRow | null>(null);

  const columns = useMemo(() => kotTrackingColumns(setSelectedRow), []);

  return (
    <>
      <ReportRecordsPage
        title="KOT tracking"
        description="Reconcile confirmed KOTs against the physical paper tickets waiters handed in. Search by KOT number, physical number, table, waiter, or who recorded it."
        searchPlaceholder="Search by KOT #, physical #, table, waiter or recorded by"
        emptyTitle="No KOTs found"
        emptyDescription="No KOTs were confirmed in this range for the current scope. Adjust the dates or switch the outlet scope to load more history."
        fetchRows={fetchKotTrackingRows}
        columns={columns}
        exportConfig={{
          fileNamePrefix: 'kot-tracking',
          columns: kotTrackingExportColumns,
        }}
        buildMetrics={buildKotTrackingMetrics}
        getRowKey={(row) => `${row.order_id}::${row.kot_id}`}
        getSearchValue={(row) =>
          [
            row.kot_id,
            row.physical_kot_number,
            row.bill_number,
            row.outlet_name,
            row.table_name,
            row.section_name,
            row.waiter_name,
            row.recorded_by_name,
          ]
            .filter(Boolean)
            .join(' ')
        }
        searchMode="server"
        buildQueryParams={({ searchQuery }) => ({
          search: searchQuery.trim() || undefined,
        })}
      />
      <KotTrackingDetailsModal
        isOpen={Boolean(selectedRow)}
        row={selectedRow}
        onClose={() => setSelectedRow(null)}
      />
    </>
  );
};
